import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Reducers } from '@app/reducers';
import { ConfigService } from '@config/config.service';
import { environment } from '@environments/environment';
import { CrmGlobalSearchDialogComponent } from '@main/components/crm-global-search-dialog/crm-global-search-dialog.component';
import { CallStateEnum } from '@main/enum/call-state-enum';
import { PeerStateEnum } from '@main/enum/peer-state-enum';
import { CallState } from '@main/models/sip-state';
import * as mainActions from '@main/ngrx/actions/main.actions';
import * as selectFromMain from '@main/ngrx/selectors/main.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { UsersService } from '@shared/services/users.service';
import { SharedService } from '@shared/shared.service';
import * as JsSIP from 'jssip';
import { RTCSession } from 'jssip/lib/RTCSession';
import { BehaviorSubject, catchError, EMPTY, interval, Observable, Subject, takeUntil } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {ManagerSimpleResponse} from "@services/back-office/model/managerSimpleResponse";
import {WebOwnProfileModel} from "@services/back-office/model/webOwnProfileModel";
import {UserInfoModel} from "@services/back-office/model/userInfoModel";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
export let dahPbx: JsSIP.UA;

@Component({
    selector: 'app-sip-dialog',
    templateUrl: './sip-dialog.component.html',
    styleUrls: ['./sip-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class SipDialogComponent implements OnInit, OnDestroy {
  /** Subject to destroy Subscriptions */
  private destroyed$ = new Subject<void>();
  /** Form */
  phone: UntypedFormControl = new UntypedFormControl('');
  dtmf: UntypedFormControl = new UntypedFormControl();
  /** SIP */
  dahPbx: JsSIP.UA;
  newRTCSession: RTCSession;
  checkInterval: any;
  /** Audio Streams */
  remoteAudio: HTMLAudioElement;
  /** SIP State */
  isReady = new BehaviorSubject(false);
  /** Call States */
  incomingCall = false;
  callInProgress = false;
  callConnected = false;
  callOnHold = false;
  callMuted = false;

  callState$: Observable<CallState>;
  callState: CallState;
  /** Call redirect */
  redirectOpened = false;
  managers: ManagerSimpleResponse[] = [];
  /** Call timer */
  callTimer: any = 0;
  callTimerString = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      currentUser?: WebOwnProfileModel | null,
      isFirstInit?: boolean
    },
    public dialogRef: MatDialogRef<SipDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    public config: ConfigService,
    private changeDetectorRef: ChangeDetectorRef,
    private store$: Store<Reducers>,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private usersService: UsersService,
    private actions$: Actions
  ) {
    this.callState$ = this.store$.pipe(select(selectFromMain.selectCallState));
  }

  ngOnInit(): void {
    this.checkInterval = interval(10000);
    // JsSIP.debug.enable('JsSIP:*');
    JsSIP.debug.disable();
    if (this.data.isFirstInit) {
      this.minimize();
    }

    this.initPhone();
    this.watchConnection();
    this.getSipStateFromObservable();
  }

  /** Sip State */
  getSipStateFromObservable() {
    this.callState$.pipe(
      filter(data => !!data),
      takeUntil(this.destroyed$),
    ).subscribe((callState: CallState) => {
      this.callState = callState;
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy() {
    clearInterval(this.checkInterval);
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  watchConnection() {
    this.checkInterval.pipe(
      take(5)
    ).subscribe(() => {
      if (!this.dahPbx.isConnected() || !this.dahPbx.isRegistered()) {
        this.initPhone();
      }
    });
  }

  initPhone() {
    this.dahPbx = new JsSIP.UA({
      sockets: [new JsSIP.WebSocketInterface(`wss://${environment.pbxUrl}:8089/ws`)],
      password: this.data.currentUser ? this.data.currentUser.sipPassword : undefined,
      uri: `sip:${this.data.currentUser!.sipLogin}@pbx.dah-online.com`
    });

    this.dahPbx.on('newRTCSession', (e: any) => {
      const newRTCSession = e.session;

      /** Terminate any existing Call */
      if (this.newRTCSession && this.newRTCSession.status !== 8) {
        this.newRTCSession.terminate();
      }

      this.newRTCSession = newRTCSession;
      this.manageCall(this.newRTCSession.direction);
    });

    this.dahPbx.start();
    /** Exported SIP Entity */
    dahPbx = this.dahPbx;

    this.watchIncomingCommand();
    if (!this.managers.length) {
      this.getManagers();
    }

    this.dahPbx.on('connected', () => {
      this.isReady.next(true);
      this.store$.dispatch(new mainActions.ChangeSipState({state: PeerStateEnum.REGISTERED}));
    });

    this.dahPbx.on('disconnected', () => {
      this.isReady.next(false);
      this.store$.dispatch(new mainActions.ChangeSipState({state: PeerStateEnum.UNREGISTERED}));
    });
  }

  resetPhone() {
    this.store$.dispatch(new mainActions.ResetCallState());
    this.stopWatchToggle(false);
    this.incomingCall = false;
    this.callConnected = false;
    this.callInProgress = false;
    this.callOnHold = false;
    this.callMuted = false;
    this.redirectOpened = false;
  }

  call(): void | boolean {
    // if (this.incomingCall) {
    //   this.newRTCSession.answer();
    //   return;
    // }

    if (this.incomingCall) {

      if (
          !this.newRTCSession ||
          this.newRTCSession.isEnded() ||
          this.newRTCSession.isEstablished()
      ) {
        return;
      }

      this.newRTCSession.answer();

      return;
    }

    if (!this.phone.value) {
      return;
    }

    const callOptions = {
      mediaConstraints: {audio: true, video: false},
    };

    this.dahPbx.call(SharedService.formatPhoneNumber(this.phone.value), callOptions);
  }

  endCall() {
    if (this.newRTCSession && this.newRTCSession.status !== 8) {
      this.newRTCSession.terminate();
    }
  }

  toggleCallMute() {
    if (this.newRTCSession && this.newRTCSession.isEstablished()) {
      this.newRTCSession.isMuted().audio
        ? this.newRTCSession.unmute()
        : this.newRTCSession.mute();
    }
  }

  toggleCallHold() {
    if (this.newRTCSession && this.newRTCSession.isEstablished()) {
      this.newRTCSession.isOnHold().local
        ? this.newRTCSession.unhold()
        : this.newRTCSession.hold();
    }
  }

  redirectCall(number?: string) {
    if (number) {
      this.sendDtmf(`##${number.substring(2)}`);
    } else {
      if (!this.dtmf.value) {
        return;
      }

      this.sendDtmf(`##${this.dtmf.value}`);
    }
  }

  sendDtmf(code: string) {
    if (code && this.newRTCSession && this.newRTCSession.isEstablished()) {
      this.newRTCSession.sendDTMF(code);
    }
  }

  manageCall(direction: string) {
    if (direction === 'incoming') {
      this.store$.dispatch(new mainActions.ChangeCallState({
        state: CallStateEnum.IN_PROGRESS,
        incomingCall: true,
        callData: {
          number: SharedService.formatPhoneNumber(this.newRTCSession.remote_identity.display_name)
        }
      }));
      this.setCallInfo(this.newRTCSession.remote_identity.display_name);
      this.incomingCall = true;
      this.callInProgress = true;
      this.changeDetectorRef.detectChanges();
      this.newRTCSession.on("peerconnection", () => {
        this.newRTCSession.connection.addEventListener("track", (e) => {
          /** Set remote audio stream (to listen to remote audio), remoteAudio is <audio> element on page */
          // @ts-ignore
          this.remoteAudio = document.querySelector('audio');
          // @ts-ignore
          this.remoteAudio.srcObject = e.streams[0];
          // @ts-ignore
          this.remoteAudio.play().then();
        });
      });
    } else {
      this.store$.dispatch(new mainActions.ChangeCallState({
        state: CallStateEnum.IN_PROGRESS,
        incomingCall: false,
        callData: {
          number: SharedService.formatPhoneNumber(this.newRTCSession.remote_identity.display_name)
        }
      }));
      this.minimize();
      this.setCallInfo(this.phone.value);
      this.incomingCall = false;
      this.callInProgress = true;
      this.changeDetectorRef.detectChanges();
      this.newRTCSession.connection.addEventListener("track", (e) => {
        /** Set remote audio stream (to listen to remote audio), remoteAudio is <audio> element on page */
        // @ts-ignore
        this.remoteAudio = document.querySelector('audio');
        // @ts-ignore
        this.remoteAudio.srcObject = e.streams[0];
        // @ts-ignore
        this.remoteAudio.play().then();
      });
    }

    this.newRTCSession.on('accepted', () => {
      this.callConnected = true;
      this.store$.dispatch(new mainActions.ChangeCallState({
        state: CallStateEnum.CONNECTED,
        incomingCall: direction === 'incoming',
        callData: {
          number: direction === 'incoming' ? SharedService.formatPhoneNumber(this.newRTCSession.remote_identity.display_name) : SharedService.formatPhoneNumber(this.phone.value)
        }
      }));
      this.stopWatchToggle(true);
      this.changeDetectorRef.detectChanges();
    });

    this.newRTCSession.on('muted', () => {
      this.store$.dispatch(new mainActions.ChangeCallState({state: CallStateEnum.MUTED}));
      this.callMuted = true;
      this.changeDetectorRef.detectChanges();
    });

    this.newRTCSession.on('unmuted', () => {
      this.store$.dispatch(new mainActions.ChangeCallState({state: CallStateEnum.CONNECTED}));
      this.callMuted = false;
      this.changeDetectorRef.detectChanges();
    });

    this.newRTCSession.on('hold', () => {
      this.store$.dispatch(new mainActions.ChangeCallState({state: CallStateEnum.ON_HOLD}));
      this.callOnHold = true;
      this.changeDetectorRef.detectChanges();
    });

    this.newRTCSession.on('unhold', () => {
      this.store$.dispatch(new mainActions.ChangeCallState({state: CallStateEnum.CONNECTED}));
      this.callOnHold = false;
      this.changeDetectorRef.detectChanges();
    });

    this.newRTCSession.on('ended', () => {
      this.resetPhone();
      this.dtmf.reset();
      this.changeDetectorRef.detectChanges();
    });

    this.newRTCSession.on('failed', () => {
      this.resetPhone();
      this.dtmf.reset();
      this.changeDetectorRef.detectChanges();
    });
  }

  /** Search user by phone number */
  setCallInfo(userPhone: string) {
    const formattedPhone = SharedService.formatPhoneNumber(userPhone);
    if (formattedPhone && formattedPhone.length && formattedPhone.length == 13) {
      this.usersService.findUser({phoneNumber: formattedPhone}).pipe(
        take(1),
        map((response: UserInfoModel) => {
          if (response?.contact) {
            this.store$.dispatch(new mainActions.ChangeCallState({callData: {number: formattedPhone, name: response.contact?.name}, personDetails: response}));
          }
        }),
        catchError(() => {
          return EMPTY;
        })
      ).subscribe();
    }
  }

  /** Take only last 10 digits from the phone clipboard data on Ctrl+v event */
  onInputPaste(clipboardData: string | undefined) {
    if (clipboardData) {
      const trimmedValue = clipboardData.replace(/\D/g, '');
      if (trimmedValue.length) {
        trimmedValue.slice(-10);
        setTimeout(() => {
          this.phone.setValue(trimmedValue);
        }, 10);
      }
    }
  }

  stopWatchToggle(action: boolean) {
    let mm = 0;
    let ss = 0;
    let ms = 0;

    if (action) {
      // Stop => Running
      this.callTimer = setInterval(() => {
        ms++;

        if (ms >= 100) {
          ss++;
          ms = 0;
        }

        if (ss >= 60) {
          mm++;
          ss = 0;
        }
        this.callTimerString = `${(mm + '').length === 1 ? '0' + mm : mm + ''}:${(ss + '').length === 1 ? '0' + ss : ss + ''}`;
        this.changeDetectorRef.detectChanges();
      }, 10);
    } else {
      clearInterval(this.callTimer);
      mm = 0;
      ss = 0;
      this.callTimerString = '00:00';
      this.changeDetectorRef.detectChanges();
    }
  }

  getManagers() {
    this.usersService.getPhonebook().pipe(
      take(1),
      map((response: ManagerSimpleResponse[]) => {
        if (response.length) {
          this.managers = response.sort((a, b) => {
            return a.sipLogin! > b.sipLogin! ? 1 : a.sipLogin! < b.sipLogin! ? -1 : 0;
          });
          this.changeDetectorRef.detectChanges();
        }
      }),
      catchError(() => {
        return EMPTY;
      })
    ).subscribe();
  }

  openCrmGlobalSearchDialog(number?: string) {
    this.sharedService.openDialog(
      CrmGlobalSearchDialogComponent,
      {...this.config.globalCrmSearch},
      {number}
    );
  }

  watchIncomingCommand() {
    this.actions$.pipe(
      ofType(mainActions.MainActionTypes.ProceedCall),
      takeUntil(this.destroyed$),
    ).subscribe((action: mainActions.ProceedCall) => {
      if (action.payload.command === 'call' && action.payload.number) {
        this.onInputPaste(action.payload.number);
        setTimeout(() => {
          this.call();
        },100);
      }

      if (action.payload.command === 'answer') {
        this.call();
      }

      if (action.payload.command === 'cancel') {
        this.endCall();
      }
    });
  }

  /** Minimize Dialog */
  minimize() {
    SharedService.minimize(
      this.dialogRef.id,
      'SIP',
      this.store$,
      undefined,
      true,
      'sip-dialog',
    );
  }

  /** Close dialog */
  closeDialog(result: boolean, data?: any) {
    const dialog$ = this.sharedService.openDialog(
      ConfirmationDialogComponent,
      {...this.config.confirmationDialog, hasBackdrop: true},
      {
        title: SharedService.getTranslation(
          this.translateService,
          'common.dialog.softPhoneDialog.closeConfirmation.title'
        ),
        submitText: 'common.dialog.softPhoneDialog.closeConfirmation.off',
        cancelText: 'common.dialog.softPhoneDialog.closeConfirmation.keep',
      }
    );

    dialog$.afterClosed().pipe(
      take(1)
    ).subscribe((result: boolean) => {
      if (result) {
        if (this.newRTCSession && this.newRTCSession.status !== 8) {
          this.newRTCSession.terminate();
        }
        this.dahPbx.stop();
        this.dahPbx.unregister({all: true});
        this.resetPhone();
        this.store$.dispatch(new mainActions.ChangeSipState({state: PeerStateEnum.UNREGISTERED}));
        this.store$.dispatch(new mainActions.CloseMinimizedDialog(this.dialogRef.id));
        this.dialogRef.close({result, data});
      } else {
        this.minimize();
      }
    });
  }
}
